import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, PencilSimple, Trash, User, MagnifyingGlass } from "@phosphor-icons/react";
import RolesCard from "@/components/users/RolesCard";

const UsersPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState({ name: "", pin: "", role: "server", email: "", hourly_rate: "" });

  const isAdmin = user?.role === "admin";

  useEffect(() => { fetchUsers(); fetchRoles(); }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${API}/roles`);
      setRoles(res.data || []);
    } catch (err) { /* dropdown falls back to empty */ }
  };

  const handleCreate = async () => {
    if (!form.pin || form.pin.length !== 4) { toast.error("PIN must be 4 digits"); return; }
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      const payload = { ...form, hourly_rate: parseFloat(form.hourly_rate) || 0 };
      await axios.post(`${API}/auth/register`, payload);
      toast.success("User created");
      setShowDialog(false);
      setForm({ name: "", pin: "", role: "server", email: "", hourly_rate: "" });
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
  };

  const handleUpdate = async () => {
    if (!editing) return;
    const updates = {};
    if (editing.name) updates.name = editing.name;
    if (editing.email) updates.email = editing.email;
    if (editing.role) updates.role = editing.role;
    if (editing.newPin && editing.newPin.length === 4) updates.pin = editing.newPin;
    if (editing.hourly_rate !== undefined && editing.hourly_rate !== null) {
      updates.hourly_rate = parseFloat(editing.hourly_rate) || 0;
    }
    try {
      await axios.put(`${API}/users/${editing.id}`, updates);
      toast.success("User updated");
      setEditing(null);
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${API}/users/${deleteConfirm.id}`);
      toast.success("User deleted");
      setDeleteConfirm(null);
      fetchUsers();
    } catch (error) { toast.error("Failed to delete"); }
  };

  const handleAdd = () => {
    setEditing(null);
    setForm({ name: "", pin: "", role: "server", email: "", hourly_rate: "" });
    setShowDialog(true);
  };

  const handleEdit = (u) => {
    setEditing({ ...u, newPin: "" });
    setShowDialog(false);
  };

  const filtered = users.filter((u) =>
    !search ||
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    (u.email || "").toLowerCase().includes(search.toLowerCase()) ||
    u.role.includes(search.toLowerCase())
  );

  const roleCounts = {
    admin: users.filter((u) => u.role === "admin").length,
    manager: users.filter((u) => u.role === "manager").length,
    server: users.filter((u) => u.role === "server").length,
    cashier: users.filter((u) => u.role === "cashier").length,
    kds: users.filter((u) => u.role === "kds").length,
  };

  return (
    <div className="min-h-screen bg-background" data-testid="users-page">
      <Sidebar />
      <main className="main-content p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-medium">Users</h1>
            <p className="text-muted-foreground">Manage staff members, roles, and PINs</p>
          </div>
          <Button className="gap-2" onClick={handleAdd} data-testid="add-user-btn">
            <Plus size={18} /> Add User
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Total Staff", count: users.length, color: "bg-primary/10 text-primary" },
            { label: "Admins", count: roleCounts.admin, color: "bg-red-100 text-red-600" },
            { label: "Managers", count: roleCounts.manager, color: "bg-blue-100 text-blue-600" },
            { label: "Servers", count: roleCounts.server + roleCounts.cashier, color: "bg-green-100 text-green-600" },
          ].map((item) => (
            <Card key={item.label}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.color}`}>
                  <User size={20} weight="fill" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                  <p className="font-mono text-xl font-medium">{item.count}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Roles management (admin only) */}
        {isAdmin && (
          <div className="mb-6">
            <RolesCard users={users} onRolesChanged={fetchRoles} />
          </div>
        )}

        {/* Search */}
        <div className="flex justify-end mb-4">
          <div className="relative w-full sm:w-64">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="users-search" />
          </div>
        </div>

        {/* User List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((u) => (
              <Card key={u.id} className="hover:shadow-sm transition-shadow" data-testid={`user-item-${u.id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-11 h-11 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                    <User size={22} className="text-primary" weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{u.name}</p>
                    <p className="text-sm text-muted-foreground">{u.email || "No email"}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {u.hourly_rate ? (
                      <span className="text-sm font-mono text-muted-foreground">${Number(u.hourly_rate).toFixed(2)}/hr</span>
                    ) : null}
                    <Badge className="bg-primary/10 text-primary border-0 capitalize">{u.role}</Badge>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(u)} data-testid={`edit-user-${u.id}`}>
                      <PencilSimple size={18} />
                    </Button>
                    {isAdmin && u.id !== user?.id && (
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => setDeleteConfirm(u)} data-testid={`delete-user-${u.id}`}>
                        <Trash size={18} />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            {filtered.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <User size={48} className="mx-auto mb-2 opacity-30" />
                  <p>No users found</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Add User Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent data-testid="add-user-dialog">
            <DialogHeader><DialogTitle className="font-heading text-xl">Add Employee</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
              <div className="space-y-2"><Label>4-Digit PIN</Label><Input type="password" maxLength={4} placeholder="0000" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })} className="font-mono text-center text-xl tracking-[8px]" data-testid="new-user-pin" /></div>
              <div className="space-y-2"><Label>Email (optional)</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
              <div className="space-y-2"><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.name} value={r.name}>{r.label || r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Hourly Rate ($)</Label><Input type="number" step="0.01" min="0" placeholder="0.00" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="new-user-hourly-rate" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button><Button onClick={handleCreate} data-testid="save-user-btn">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
          <DialogContent data-testid="edit-user-dialog">
            <DialogHeader><DialogTitle className="font-heading text-xl">Edit Employee</DialogTitle></DialogHeader>
            {editing && (
              <div className="space-y-4 py-4">
                <div className="space-y-2"><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-user-name" /></div>
                <div className="space-y-2"><Label>Email</Label><Input value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="edit-user-email" /></div>
                <div className="space-y-2"><Label>Role</Label>
                  <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                    <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {roles.map((r) => (
                        <SelectItem key={r.name} value={r.name}>{r.label || r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>New PIN (leave empty to keep current)</Label><Input type="password" maxLength={4} placeholder="****" value={editing.newPin} onChange={(e) => setEditing({ ...editing, newPin: e.target.value.replace(/\D/g, "").slice(0, 4) })} className="font-mono text-center text-xl tracking-[8px]" data-testid="edit-user-pin" /></div>
                <div className="space-y-2"><Label>Hourly Rate ($)</Label><Input type="number" step="0.01" min="0" placeholder="0.00" value={editing.hourly_rate ?? ""} onChange={(e) => setEditing({ ...editing, hourly_rate: e.target.value })} data-testid="edit-user-hourly-rate" /></div>
              </div>
            )}
            <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button onClick={handleUpdate} data-testid="save-edit-user-btn">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="sm:max-w-[400px]" data-testid="delete-user-confirm">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Delete User</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground py-2">
              Are you sure you want to delete <span className="font-medium text-foreground">{deleteConfirm?.name}</span>? This action cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} data-testid="cancel-delete-btn">Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default UsersPage;
